import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import ReadingProgress from "@/components/ReadingProgress";
import { ArrowLeft, ArrowRight } from "lucide-react";

/**
 * Markdown → HTML léger avec IDs sur les H2/H3 (utilisé pour l'ancrage optionnel).
 */
function slugify(s) {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}
function renderMd(md) {
  const lines = md.split(/\r?\n/);
  const out = [];
  let inList = false, inTable = false, tableRows = [];
  const flushList = () => { if (inList) { out.push("</ul>"); inList = false; } };
  const flushTable = () => {
    if (!inTable) return;
    const [head, ...rest] = tableRows;
    const heads = head.split("|").map((s) => s.trim()).filter(Boolean);
    const bodyRows = rest.filter((r) => !/^\s*\|?\s*[-|: ]+\|/.test(r));
    out.push("<table><thead><tr>" + heads.map((h) => `<th>${h}</th>`).join("") + "</tr></thead><tbody>");
    for (const r of bodyRows) {
      const cells = r.split("|").map((s) => s.trim()).filter((c, i, a) => !(i === 0 && c === "") && !(i === a.length - 1 && c === ""));
      out.push("<tr>" + cells.map((c) => `<td>${inline(c)}</td>`).join("") + "</tr>");
    }
    out.push("</tbody></table>");
    tableRows = []; inTable = false;
  };
  const inline = (s) => s
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");

  for (const raw of lines) {
    const line = raw;
    if (/^\s*$/.test(line)) { flushList(); flushTable(); continue; }
    if (/^\|/.test(line.trim())) { flushList(); inTable = true; tableRows.push(line.trim()); continue; }
    else if (inTable) { flushTable(); }
    if (/^## /.test(line)) {
      flushList();
      const text = line.replace(/^## /, "");
      out.push(`<h2 id="${slugify(text)}">${inline(text)}</h2>`);
      continue;
    }
    if (/^### /.test(line)) {
      flushList();
      const text = line.replace(/^### /, "");
      out.push(`<h3 id="${slugify(text)}">${inline(text)}</h3>`);
      continue;
    }
    if (/^---\s*$/.test(line)) { flushList(); out.push("<hr />"); continue; }
    if (/^- /.test(line)) {
      if (!inList) { out.push("<ul>"); inList = true; }
      out.push(`<li>${inline(line.replace(/^- /, ""))}</li>`);
      continue;
    }
    flushList();
    out.push(`<p>${inline(line)}</p>`);
  }
  flushList(); flushTable();
  return out.join("\n");
}

/**
 * Extrait des paires Q/A pour FAQPage schema.
 * Heuristique : tout heading H2/H3 (## ou ###) qui se termine par un point d'interrogation,
 * suivi de paragraphes jusqu'au prochain heading ou hr.
 */
function extractFaq(md) {
  const lines = md.split(/\r?\n/);
  const faqs = [];
  let current = null;
  const flush = () => { if (current && current.answer.trim()) faqs.push(current); current = null; };
  for (const raw of lines) {
    const line = raw.trim();
    const headMatch = /^(#{2,3})\s+(.+)$/.exec(line);
    if (headMatch) {
      const q = headMatch[2].trim();
      if (/\?\s*$/.test(q)) {
        flush();
        current = { question: q, answer: "" };
      } else {
        flush();
      }
      continue;
    }
    if (/^---\s*$/.test(line)) { flush(); continue; }
    if (current) {
      if (/^\s*$/.test(line)) continue;
      if (/^- /.test(line)) current.answer += "\n" + line.replace(/^- /, "• ");
      else current.answer += " " + line;
    }
  }
  flush();
  return faqs.slice(0, 8);
}

export default function BlogDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    api.get(`/articles/${slug}`).then(({ data }) => setA(data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!a) return;
    api.get("/articles").then(({ data }) => {
      const others = data.filter((x) => x.slug !== a.slug);
      // Priorité même catégorie, puis les autres
      const sameCat = others.filter((x) => x.category === a.category);
      const rest = others.filter((x) => x.category !== a.category);
      setRelated([...sameCat, ...rest].slice(0, 3));
    }).catch(() => {});
  }, [a]);

  const faqSchema = useMemo(() => {
    if (!a) return null;
    const faqs = extractFaq(a.content || "");
    if (faqs.length < 2) return null;
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer.trim() },
      })),
    };
  }, [a]);

  if (notFound) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-navy/60">Article introuvable. <Link to="/blog" className="underline">Retour aux guides</Link></div>;
  if (!a) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-navy/60">Chargement...</div>;

  return (
    <>
      <SEO title={`${a.title} — venteaterme.fr`} description={a.excerpt} schema={faqSchema} />
      <ReadingProgress />

      <section className="pt-8 pb-6">
        <div className="max-w-3xl mx-auto px-6">
          <Breadcrumbs items={[
            { href: "/", label: "Accueil" },
            { href: "/blog", label: "Guides" },
            { href: `/blog/${a.slug}`, label: a.title },
          ]} />
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-accent-brand mb-8">
            <ArrowLeft size={16} /> Retour aux guides
          </Link>
          <div className="text-[10px] uppercase tracking-[0.2em] text-accent-brand font-bold mb-4">{a.category}</div>
          <h1 className="font-serif text-4xl md:text-5xl text-navy leading-tight tracking-tight text-balance">{a.title}</h1>
          <div className="text-sm text-navy/60 mt-6">Par {a.author} · {a.reading_time} min de lecture</div>
        </div>
      </section>

      <section>
        <div className="max-w-3xl mx-auto px-6">
          <div className="aspect-[16/8] overflow-hidden bg-navy/5 my-10">
            <img src={a.cover_image} alt={a.title} width="1200" height="600" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-6 pb-16">
          <div className="prose-vt" dangerouslySetInnerHTML={{ __html: renderMd(a.content) }} />
        </div>

        {related.length > 0 && (
          <div className="max-w-3xl mx-auto px-6 pb-24">
            <div className="border-t border-navy/10 pt-10">
              <div className="text-xs uppercase tracking-[0.2em] text-accent-brand font-bold mb-4">Poursuivre la lecture</div>
              <h2 className="font-serif text-2xl md:text-3xl text-navy mb-8 text-balance">Guides liés à cet article</h2>
              <div className="grid sm:grid-cols-3 gap-5">
                {related.map((r) => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} data-testid={`related-article-${r.slug}`}
                    className="group block bg-white border border-navy/10 card-lift p-5">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-accent-brand font-bold mb-2">{r.category}</div>
                    <div className="font-serif text-lg text-navy leading-snug mb-2 group-hover:text-accent-brand transition-colors">{r.title}</div>
                    <div className="text-xs text-navy/50 mt-3 flex items-center gap-1">
                      Lire le guide <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
